import { useState } from 'react';
import { Briefcase, Plus } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { OpenVacancyEntry } from '@hygge/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useOpenVacancies } from '@/hooks/use-dashboard';
import { CreateVacancyDialog } from '@/features/recruitment/components/create-vacancy-dialog';
import { WidgetCard } from '../widget-card';
import { ExpandableList } from '../expandable-list';
import { Chip, type ChipColor } from '../chip';

const PRIORITY_LABEL: Record<OpenVacancyEntry['priority'], string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};
const PRIORITY_COLOR: Record<OpenVacancyEntry['priority'], ChipColor> = {
  LOW: 'gray',
  MEDIUM: 'amber',
  HIGH: 'red',
  CRITICAL: 'red',
};
/** High/Critical vacancies open this long flag an SLA risk. */
const SLA_RISK_DAYS = 45;

function VacancyRow({ v }: { v: OpenVacancyEntry }) {
  const slaRisk = (v.priority === 'HIGH' || v.priority === 'CRITICAL') && v.daysOpen >= SLA_RISK_DAYS;

  return (
    <Link
      to="/recruitment/vacancies/$id"
      params={{ id: v.id }}
      className="dashboard-row group flex items-center gap-3 border-b border-border py-2.5 last:border-0"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium group-hover:underline">{v.title}</div>
        <div className="truncate text-xs text-muted-foreground">
          {[v.department, `${v.applicantCount} applicant${v.applicantCount === 1 ? '' : 's'}`]
            .filter(Boolean)
            .join(' · ')}{' '}
          ·{' '}
          <span className={cn('tabular-nums', slaRisk && 'font-medium text-destructive')}>
            {v.daysOpen} days open
          </span>
        </div>
      </div>
      <Chip color={PRIORITY_COLOR[v.priority]}>{PRIORITY_LABEL[v.priority]}</Chip>
    </Link>
  );
}

/**
 * `openVacancies` widget — the caller's active vacancies (recruiter → own
 * assigned; lead/super_admin → all), longest-open first, each linking to the vacancy
 * page. Header "+ Add vacancy" opens the shared create-vacancy dialog.
 */
export function OpenVacanciesWidget() {
  const { data, isLoading, isError } = useOpenVacancies();
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const rows = data ?? [];

  return (
    <>
      <WidgetCard
        title="Open Vacancies"
        icon={Briefcase}
        action={
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            <Plus className="h-3.5 w-3.5" /> Add vacancy
          </button>
        }
      >
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
                <Skeleton className="h-4 w-14" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <p className="py-4 text-center text-xs text-muted-foreground">
            Couldn&apos;t load open vacancies.
          </p>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">
            No open vacancies assigned to you.
          </p>
        ) : (
          <ExpandableList
            items={rows}
            getKey={(v) => v.id}
            renderItem={(v: OpenVacancyEntry) => <VacancyRow v={v} />}
          />
        )}
      </WidgetCard>
      <CreateVacancyDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={(id) => {
          setDialogOpen(false);
          // A freshly-created vacancy is a DRAFT (so it wouldn't show in this ACTIVE-only
          // list); open it so the user can finish setting it up — mirrors the Vacancies page.
          navigate({ to: '/recruitment/vacancies/$id', params: { id } });
        }}
      />
    </>
  );
}
